import { useState, useRef, useEffect, useMemo } from "react";
import { Flex, Input } from "antd";
import {
  SendOutlined,
  CloseOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "../../axios-interceptor.js";
import SvgIcons from "../layout/assets/SvgIcons.jsx";
import { createDispatcher } from "./dispatcher.js";
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const GREETING_KEYWORDS = [
  "hello", "hi", "hey", "good morning", "good afternoon", "good evening",
  "greetings", "howdy", "what's up", "how are you",
];

const GRATITUDE_KEYWORDS = [
  "thanks", "thank you", "thank u", "thx", "ty",
  "perfect", "great", "awesome", "amazing", "wonderful",
  "well done", "good job", "nice", "excellent", "fantastic",
  "appreciated", "appreciate it", "much appreciated",
];

const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function matchesKeyword(text, keywords) {
  const lower = text.toLowerCase().trim();
  return keywords.some(kw => new RegExp(`\\b${kw.trim()}\\b`, "i").test(lower));
}

const isGreeting = (t) => matchesKeyword(t, GREETING_KEYWORDS);
const isGratitude = (t) => matchesKeyword(t, GRATITUDE_KEYWORDS);

/**
 * Unified AI Assistant chatbot. Shared between form-builder and workflow-builder.
 *
 * The host page controls scope and behavior via props:
 *
 * @param {string}   title             Header label (e.g. "AI Assistant").
 * @param {string}   welcomeMessage    Greeting shown when panel first opens.
 * @param {string}   placeholder       Input placeholder text.
 * @param {string}   apiUrl            Unified bot endpoint (POST).
 * @param {string[]} allowedDomains    Domains this host can mutate (see dispatcher).
 * @param {object}   handlers          Map of "domain:action" -> applier (see dispatcher).
 * @param {function} buildPayload      async (prompt) => request body. Host assembles
 *                                     formItems / workflowNodes / conditions / space data.
 * @param {function} [onAfterDispatch] Optional callback (dispatchResult) => void.
 *                                     Use for cache invalidation, toasts, etc.
 * @param {object}   [fileUpload]      Optional { uploadUrl, onApplyExtracted }.
 *                                     When provided, the paperclip button appears and
 *                                     uploads go to uploadUrl; the response is fed to
 *                                     onApplyExtracted(result) => { ok, message? }.
 * @param {Array}    [extraCommands]   Optional shortcut commands, evaluated before the
 *                                     API call. Each item: { match: (text) => boolean,
 *                                     run: async (text) => { ok, message?, statusText? } }.
 *                                     Useful for "save workflow" / "summarize" intents.
 */
const AssistantBot = ({
  title = "AI Assistant",
  welcomeMessage = "Hi! How can I help you?",
  placeholder = "Describe what you want to do…",
  apiUrl,
  allowedDomains,
  handlers,
  buildPayload,
  onAfterDispatch,
  fileUpload,
  extraCommands,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState("");
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const messageListRef = useRef(null);
  const fileInputRef = useRef(null);

  const dispatch = useMemo(
    () => createDispatcher({ handlers, allowedDomains }),
    [handlers, allowedDomains]
  );

  const generateMessageId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const getCurrentTimestamp = () => dayjs().format("HH:mm");

  const appendMessage = (role, text, status = null, statusText = null) => {
    setMessages(prev => [
      ...prev,
      { id: generateMessageId(), role, text, status, statusText, timestamp: getCurrentTimestamp() },
    ]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = messageListRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
    return () => clearTimeout(timer);
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      appendMessage("ai", welcomeMessage, "ok");
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      appendMessage("ai", "Only image (PNG, JPG, WebP) and PDF files are allowed.", "error");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      appendMessage("ai", "File size must be less than 10MB.", "error");
      return;
    }
    setSelectedFile(file);
    e.target.value = "";
  };

  const handleRemoveFile = () => setSelectedFile(null);

  const uploadFile = async (file, prompt) => {
    if (!fileUpload?.uploadUrl) throw new Error("File upload is not configured for this page.");
    const formData = new FormData();
    formData.append("field", file);
    // When the user types alongside the attachment, forward the prompt too.
    if (prompt) formData.append("prompt", prompt);
    // Include the current form items (if any) so the AI can edit in place
    // rather than replacing an existing form. Sourced from the host payload.
    if (buildPayload) {
      try {
        const payload = await buildPayload(prompt || "");
        if (Array.isArray(payload?.formItems) && payload.formItems.length > 0) {
          formData.append("formItems", JSON.stringify(payload.formItems));
        }
      } catch (err) {
      }
    }
    const tenantId = localStorage.getItem("tenantId");
    const response = await axios({
      method: "POST",
      url: fileUpload.uploadUrl,
      data: formData,
      headers: { tenantId, "Content-Type": "multipart/form-data" },
    });
    return response.data;
  };

  const callBotApi = async (prompt) => {
    if (!apiUrl) throw new Error("Assistant API URL is not configured.");
    if (!buildPayload) throw new Error("buildPayload is not configured.");
    const body = await buildPayload(prompt);
    const response = await axios({ method: "POST", url: apiUrl, data: body });
    return response.data;
  };

  const respondAsAi = async (trimmed) => {
    setIsLoading(true);
    try {
      if (isGreeting(trimmed)) {
        appendMessage("ai", "Hello, how can I help you?");
        return;
      }
      if (isGratitude(trimmed)) {
        appendMessage("ai", "You're welcome! Let me know if there's anything else I can help you with.");
        return;
      }

      if (Array.isArray(extraCommands)) {
        const cmd = extraCommands.find(c => c.match?.(trimmed));
        if (cmd) {
          const r = await cmd.run(trimmed);
          appendMessage(
            "ai",
            r?.message || (r?.ok ? "Done." : "Something went wrong. Please try again."),
            r?.ok ? "ok" : "error",
            r?.statusText || (r?.ok ? "Completed successfully!" : "Something went wrong."),
          );
          return;
        }
      }

      const response = await callBotApi(trimmed);
      const result = await dispatch(response);
      onAfterDispatch?.(result);

      const status = result.ok ? "ok" : result.skipped ? null : "error";
      const statusText = result.ok
        ? "Changes applied successfully!"
        : result.skipped
          ? null
          : "Something went wrong.";
      appendMessage("ai", result.message, status, statusText);
    } catch (error) {
      appendMessage(
        "ai",
        `An error occurred: ${error?.message || "Unknown error"}. Please try again.`,
        "error",
        "Something went wrong.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    const trimmed = inputText.trim();
    const file = selectedFile;
    if ((!trimmed && !file) || isLoading) return;

    appendMessage(
      "user",
      file ? `📎 ${file.name}${trimmed ? `\n${trimmed}` : ""}` : trimmed,
    );
    setInputText("");
    setSelectedFile(null);

    if (file) {
      setIsLoading(true);
      try {
        const result = await uploadFile(file, trimmed);
        const applyResult = fileUpload?.onApplyExtracted ? await fileUpload.onApplyExtracted(result) : null;
        appendMessage(
          "ai",
          applyResult?.message || result?.message || "Done! I've extracted and applied the content from your file.",
          "ok",
          "File processed successfully!",
        );
      } catch (error) {
        appendMessage(
          "ai",
          `An error occurred: ${error?.message || "Unknown error"}. Please try again.`,
          "error",
          "Something went wrong.",
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    await respondAsAi(trimmed);
  };

  const handleCopyMessage = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg.text);
      setCopiedMessageId(msg.id);
      setTimeout(() => {
        setCopiedMessageId(prev => (prev === msg.id ? null : prev));
      }, 1500);
    } catch (err) {
    }
  };

  const handleEditStart = (msg) => {
    setEditingMessageId(msg.id);
    setEditText(msg.text);
  };

  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditText("");
  };

  const handleEditSave = async (msg) => {
    const trimmed = editText.trim();
    if (!trimmed || isLoading) return;
    setMessages(prev => prev.map(m => (m.id === msg.id ? { ...m, text: trimmed } : m)));
    setEditingMessageId(null);
    setEditText("");
    await respondAsAi(trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ai-bot-container">
      {isOpen && (
        <div className="ai-bot-panel">
          <div className="ai-bot-header">
            <Flex align="center" gap={8}>
              <div className="ai-bot-header-avatar">
                <div className="ai-bot-header-avatar-inner">
                  <SvgIcons type="chatbot" />
                </div>
              </div>
              <span className="ai-bot-header-title">{title}</span>
            </Flex>
          </div>

          <div className="ai-bot-messages" ref={messageListRef}>
            {messages.map(msg => {
              const isEditing = editingMessageId === msg.id;
              const isFileMsg = msg.role === "user" && msg.text?.startsWith("📎");
              return (
                <div key={msg.id} className={`ai-bot-message-row ai-bot-message-row--${msg.role}`}>
                  {isEditing ? (
                    <div className="ai-bot-edit-panel">
                      <TextArea
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        autoFocus
                      />
                      <div className="ai-bot-edit-actions">
                        <button
                          type="button"
                          className="ai-bot-edit-btn ai-bot-edit-btn--cancel"
                          onClick={handleEditCancel}
                        >
                          {t('Cancel')}
                        </button>
                        <button
                          type="button"
                          className="ai-bot-edit-btn ai-bot-edit-btn--save"
                          onClick={() => handleEditSave(msg)}
                          disabled={isLoading || !editText.trim()}
                        >
                          {t('Send')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="ai-bot-bubble">
                        <div className="ai-bot-bubble-text">{msg.text}</div>
                        {msg.role === "ai" && msg.statusText && (
                          <>
                            <div className="ai-bot-bubble-divider" />
                            <div className={`ai-bot-bubble-status ai-bot-bubble-status--${msg.status === "error" ? "error" : "ok"}`}>
                              {msg.status === "error" ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                              <span>{msg.statusText}</span>
                            </div>
                          </>
                        )}
                      </div>
                      {msg.role === "user" && (
                        <div className="ai-bot-msg-actions">
                          {!isFileMsg && (
                            <EditOutlined
                              className="ai-bot-msg-action"
                              onClick={() => handleEditStart(msg)}
                              title={t('Edit message')}
                            />
                          )}
                          {copiedMessageId === msg.id ? (
                            <CheckOutlined className="ai-bot-msg-action ai-bot-msg-action--copied" title={t('Copied')} />
                          ) : (
                            <CopyOutlined
                              className="ai-bot-msg-action"
                              onClick={() => handleCopyMessage(msg)}
                              title={t('Copy')}
                            />
                          )}
                        </div>
                      )}
                      <div className="ai-bot-timestamp">{msg.timestamp}</div>
                    </>
                  )}
                </div>
              );
            })}

            {isLoading && (
              <div className="ai-bot-message-row ai-bot-message-row--ai">
                <div className="ai-bot-bubble ai-bot-bubble--typing">
                  <span className="ai-bot-typing-dot" />
                  <span className="ai-bot-typing-dot" />
                  <span className="ai-bot-typing-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="ai-bot-input-area">
            <div className="ai-bot-input-wrapper">
              {selectedFile && (
                <div className="ai-bot-file-preview">
                  <span className="ai-bot-file-name">
                    <PaperClipOutlined /> {selectedFile.name}
                  </span>
                  <DeleteOutlined className="ai-bot-file-remove" onClick={handleRemoveFile} />
                </div>
              )}
              <div className="ai-bot-input-row">
                {fileUpload && (
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept=".png,.jpg,.jpeg,.webp,.pdf"
                    onChange={handleFileSelect}
                  />
                )}
                <TextArea
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={isLoading}
                />
                {fileUpload && (
                  <div
                    className={`ai-bot-attach ${isLoading ? "ai-bot-attach--disabled" : ""}`}
                    onClick={() => !isLoading && fileInputRef.current?.click()}
                    title={t('Upload image or PDF')}
                  >
                    <PaperClipOutlined style={{ color: "grey" }} />
                  </div>
                )}
                <div
                  className={`ai-bot-send ${isLoading ? "ai-bot-send--disabled" : ""}`}
                  onClick={handleSend}
                >
                  <SendOutlined style={{ marginLeft: "2px" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="ai-bot-toggle"
        onClick={() => setIsOpen(prev => !prev)}
        title={title}
      >
        {isOpen
          ? <CloseOutlined style={{ color: "#fff", fontSize: 18 }} />
          : <SvgIcons type="chatbot" style={{ color: "#fff", fontSize: 20 }} />}
      </div>
    </div>
  );
};

export default AssistantBot;
